<script setup>
import pricing from "~/data/pricing-features.json";
import { foundingFirstYear, foundingSignupUrl } from "~/data/founding-offer";

const props = defineProps({
  location: { type: String, required: true },
});

const { addReferralToUrl } = useReferral();
const { localePath } = useWorkspaceLocale();

// Founding 100 offer (data/founding-offer.ts): the founding first year on yearly billing
// under each monthly list price (the offer is yearly only), until the client sees the deadline has passed.
const founding = useFoundingOffer();
const showFounding = computed(() => founding.enabled && !founding.expired.value);
const signupUrl = (slug) => {
  const href = `https://app.workspace369.com/signup?plan=${slug}`;
  return addReferralToUrl(founding.enabled ? foundingSignupUrl(href) : href);
};

const extraSeat = Number(
  pricing.categories.flatMap((c) => c.rows).find((r) => r.key === "additionalSeats").cells[1].replace("$", ""),
);
const plans = pricing.plans.filter((p) => ["voyager", "specialist", "commander"].includes(p.slug));
const fit = { voyager: "Small-team essentials", specialist: "Most agencies", commander: "Larger operations" };
</script>

<template>
  <section id="team-pricing" class="team-offer" data-sticky-cta-hide aria-labelledby="team-offer-title">
    <div class="mx-auto max-w-6xl px-4 py-14 sm:px-6 md:py-16">
      <div class="team-offer-heading">
        <h2 id="team-offer-title">Priced for the team, not per person.</h2>
        <span>Each team plan includes seats. Retainers, recurring invoices, proposals and client records are on every plan. Extra seats are ${{ extraSeat }} a month each.</span>
      </div>
      <div class="team-offer-grid">
        <article v-for="plan in plans" :key="plan.slug" :class="{ featured: plan.slug === 'specialist' }">
          <small>{{ fit[plan.slug] }}</small>
          <h3>{{ plan.name }}</h3>
          <p class="team-offer-price">
            <strong>${{ plan.monthly }}</strong> / month
          </p>
          <p v-if="showFounding" class="team-offer-founding" data-founding-offer>Founding offer: ${{ foundingFirstYear(String(plan.monthly)) }} for your first year, billed yearly</p>
          <p class="team-offer-seats">{{ plan.seats }} seats included</p>
          <a
            :href="signupUrl(plan.slug)"
            data-conversion-event="start_free_click"
            :data-conversion-label="`Try ${plan.name}`"
            :data-conversion-location="`${props.location}-team-${plan.slug}`"
            :data-conversion-plan="plan.slug"
          >Start {{ plan.name }} trial <span aria-hidden="true">→</span></a>
          <TrialDisclosure :plan="plan.slug" />
        </article>
      </div>
      <p class="team-offer-note">
        <span v-if="showFounding" data-founding-offer>Founding member offer on yearly plans: your first year costs the same as 6 months, for the first 100 customers who join by Oct 31. </span>USD monthly billing.
        <NuxtLink :to="localePath('/pricing')" data-conversion-event="pricing_click" data-conversion-label="Compare all plans" :data-conversion-location="`${props.location}-team`">Compare all plans</NuxtLink>.
        Prefer a guided walkthrough first?
        <NuxtLink :to="localePath('/contact-sales')" data-conversion-event="demo_request_click" data-conversion-label="Book a demo" :data-conversion-location="`${props.location}-team`">Book a demo</NuxtLink>.
      </p>
    </div>
    <SwitchSave :location="props.location" />
  </section>
</template>

<style scoped>
.team-offer { scroll-margin-top: 120px; padding-bottom: 1rem; background: #fff; color: #0f172a; }
.dark .team-offer { background: #020617; color: #fff; }
.team-offer-heading { max-width: 44rem; }
.team-offer-heading h2 { margin-top: 0; font-size: clamp(1.75rem, 3vw, 2.4rem); font-weight: 800; line-height: 1.15; }
.team-offer-heading span { display: block; margin-top: 0.8rem; color: #475569; line-height: 1.7; }
.dark .team-offer-heading span { color: #cbd5e1; }
.team-offer-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; margin-top: 2rem; }
.team-offer-grid article { border: 1px solid #e2e8f0; border-radius: 10px; padding: 1.4rem; background: #fff; display: flex; flex-direction: column; }
.dark .team-offer-grid article { border-color: rgb(255 255 255 / 0.12); background: rgb(255 255 255 / 0.04); }
.team-offer-grid article.featured { border-color: #0e7490; box-shadow: 0 18px 38px -28px #0e7490; }
.team-offer-grid small { color: #64748b; font-size: 0.8rem; }
.dark .team-offer-grid small { color: #94a3b8; }
.team-offer-grid h3 { margin-top: 0.4rem; font-size: 1.4rem; font-weight: 800; }
.team-offer-price { margin-top: 0.6rem; color: #64748b; }
.team-offer-price strong { color: #0f172a; font-size: 2rem; font-weight: 800; }
.dark .team-offer-price { color: #cbd5e1; }
.dark .team-offer-price strong { color: #fff; }
.team-offer-founding { margin-top: 0.15rem; color: #0e7490; font-size: 0.82rem; font-weight: 700; }
.dark .team-offer-founding { color: #67e8f9; }
.team-offer-seats { margin-top: 0.2rem; font-weight: 600; }
.team-offer-grid a { margin-top: 1.2rem; display: inline-flex; justify-content: center; align-items: center; gap: 0.4rem; min-height: 2.9rem; border-radius: 0.375rem; border: 1px solid #cbd5e1; font-weight: 700; font-size: 0.92rem; }
.team-offer-grid article.featured a { background: #0e7490; border-color: #0e7490; color: #fff; }
.team-offer-grid a:hover { border-color: #94a3b8; }
.team-offer-grid article.featured a:hover { background: #155e75; }
.dark .team-offer-grid a { border-color: rgb(255 255 255 / 0.18); }
.team-offer-note { margin-top: 1.2rem; font-size: 0.9rem; color: #64748b; line-height: 1.7; }
.dark .team-offer-note { color: #94a3b8; }
.team-offer-note a { text-decoration: underline; text-underline-offset: 3px; color: inherit; }
@media (max-width: 760px) { .team-offer-grid { grid-template-columns: 1fr; } }
</style>
